import React, { useState } from "react";
import { createQuestion } from "../utils/assessmentUtils";
import { SVG_ICONS } from "./TablerIconPicker";
import "../styles/AssessmentEditor.css";

const AssessmentEditModal = ({ assessment, onSave, onCancel }) => {
  const [activeTab, setActiveTab] = useState("details");
  const [previewAnswers, setPreviewAnswers] = useState({});
  const [formData, setFormData] = useState({
    ...assessment,
    questions: assessment.questions || [],
    scoreBands: assessment.scoreBands || [],
    version: assessment.version || 1,
    parentId: assessment.parentId || null,
    isLocked: assessment.isLocked || false
  });

  const handleMetadataChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddQuestion = () => {
    const newQuestion = createQuestion("scored");
    setFormData((prev) => ({
      ...prev,
      questions: [...(prev.questions || []), newQuestion]
    }));
  };

  const handleDeleteQuestion = (questionId) => {
    if (!window.confirm("Delete this question?")) return;
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.filter((q) => q.id !== questionId)
    }));
  };

  const handleQuestionChange = (questionId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.map((q) =>
        q.id === questionId ? { ...q, [field]: value } : q
      )
    }));
  };

  const handleOptionChange = (questionId, optionIndex, field, value) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.map((q) => {
        if (q.id === questionId) {
          const newOpts = [...q.opts];
          newOpts[optionIndex] = { ...newOpts[optionIndex], [field]: value };
          return { ...q, opts: newOpts };
        }
        return q;
      })
    }));
  };

  const handleAddOption = (questionId) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.map((q) => {
        if (q.id === questionId) {
          return {
            ...q,
            opts: [
              ...q.opts,
              q.type === "scored"
                ? { label: "", score: Math.max(...q.opts.map(o => o.score || 1)) + 1 }
                : { label: "" }
            ]
          };
        }
        return q;
      })
    }));
  };

  const handleDeleteOption = (questionId, optionIndex) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.map((q) => {
        if (q.id === questionId && q.opts.length > 1) {
          const newOpts = q.opts.filter((_, i) => i !== optionIndex);
          return { ...q, opts: newOpts };
        }
        return q;
      })
    }));
  };

  const handleMoveQuestion = (questionId, direction) => {
    const index = formData.questions.findIndex((q) => q.id === questionId);
    if (index === -1) return;

    const newQuestions = [...formData.questions];
    if (direction === "up" && index > 0) {
      [newQuestions[index], newQuestions[index - 1]] = [
        newQuestions[index - 1],
        newQuestions[index]
      ];
    } else if (direction === "down" && index < newQuestions.length - 1) {
      [newQuestions[index], newQuestions[index + 1]] = [
        newQuestions[index + 1],
        newQuestions[index]
      ];
    }

    setFormData((prev) => ({
      ...prev,
      questions: newQuestions
    }));
  };

  const handleToggleLive = () => {
    setFormData((prev) => ({
      ...prev,
      live: !prev.live
    }));
  };

  const handleSave = () => {
    if (!formData.name.trim()) {
      alert("Assessment name is required");
      return;
    }
    onSave(formData);
  };

  return (
    <div className="assessment-editor">
      <div className="editor-container">
        <h3 style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {SVG_ICONS["clipboard-list"]({ size: 18, color: "#5B9EF0" })}
          {formData.live ? "LIVE" : "DRAFT"} — {formData.name}
        </h3>

        {/* Tab Navigation */}
        <div className="editor-tabs">
          <button
            className={`editor-tab ${activeTab === "details" ? "active" : ""}`}
            onClick={() => setActiveTab("details")}
            style={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            {SVG_ICONS["settings"]({ size: 16, color: "currentColor" })}
            Details
          </button>
          <button
            className={`editor-tab ${activeTab === "questions" ? "active" : ""}`}
            onClick={() => setActiveTab("questions")}
            style={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            {SVG_ICONS["help-circle"]({ size: 16, color: "currentColor" })}
            Questions ({formData.questions?.length || 0})
          </button>
          <button
            className={`editor-tab ${activeTab === "bands" ? "active" : ""}`}
            onClick={() => setActiveTab("bands")}
            style={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            {SVG_ICONS["palette"]({ size: 16, color: "currentColor" })}
            Score Bands ({formData.scoreBands?.length || 0})
          </button>
          <button
            className={`editor-tab ${activeTab === "versioning" ? "active" : ""}`}
            onClick={() => setActiveTab("versioning")}
            style={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            {SVG_ICONS["git-branch"]({ size: 16, color: "currentColor" })}
            Versioning
          </button>
          <button
            className={`editor-tab ${activeTab === "preview" ? "active" : ""}`}
            onClick={() => setActiveTab("preview")}
            style={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            {SVG_ICONS["eye"]({ size: 16, color: "currentColor" })}
            Preview
          </button>
        </div>

        {/* Details Tab */}
        {activeTab === "details" && (
        <div className="editor-section">
          <h4>Assessment Details</h4>

          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleMetadataChange}
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleMetadataChange}
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.allowRetake}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    allowRetake: e.target.checked
                  }))
                }
              />
              Allow users to retake
            </label>
          </div>

          <div className="form-group">
            <label>Show Results</label>
            <select
              value={formData.showResults}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  showResults: e.target.value
                }))
              }
            >
              <option value="both">Score & Answers</option>
              <option value="score">Score Only</option>
              <option value="answers">Answers Only</option>
              <option value="none">No Results</option>
            </select>
          </div>
        </div>
        )}

        {/* Score Bands Tab */}
        {activeTab === "bands" && (
        <div className="editor-section">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <h4>Score Bands ({formData.scoreBands?.length || 0})</h4>
            <button
              className="btn-add-question"
              onClick={() => {
                setFormData((prev) => ({
                  ...prev,
                  scoreBands: [
                    ...(prev.scoreBands || []),
                    {
                      min: 1,
                      max: 2,
                      label: "New Band",
                      color: "#5B9EF0",
                      description: ""
                    }
                  ]
                }));
              }}
            >
              + Add Band
            </button>
          </div>

          {formData.scoreBands?.length === 0 ? (
            <p style={{ color: "#7A9CC4", fontSize: "13px" }}>
              Optional: Define score ranges and labels for displaying results.
            </p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {formData.scoreBands.map((band, idx) => (
                <div key={idx} style={{ background: "#050e1a", padding: "16px", borderRadius: "8px", border: `2px solid ${band.color}40` }}>
                  <div style={{ display: "flex", gap: "16px", marginBottom: "12px", alignItems: "flex-start" }}>
                    {/* Color Preview */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "center" }}>
                      <div
                        style={{
                          width: "80px",
                          height: "80px",
                          background: band.color,
                          borderRadius: "8px",
                          border: "2px solid rgba(255,255,255,0.2)",
                          boxShadow: `0 0 16px ${band.color}40`
                        }}
                      />
                      <label style={{ fontSize: "12px", color: "#7A9CC4" }}>Color</label>
                      <input
                        type="color"
                        value={band.color}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            scoreBands: prev.scoreBands.map((b, i) =>
                              i === idx ? { ...b, color: e.target.value } : b
                            )
                          }))
                        }
                        style={{ width: "60px", height: "40px", cursor: "pointer", borderRadius: "4px", border: "none" }}
                      />
                    </div>

                    {/* Range and Label */}
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: "8px", marginBottom: "12px" }}>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label style={{ fontSize: "12px" }}>Min</label>
                          <input
                            type="number"
                            value={band.min}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                scoreBands: prev.scoreBands.map((b, i) =>
                                  i === idx ? { ...b, min: parseFloat(e.target.value) || 0 } : b
                                )
                              }))
                            }
                            step="0.1"
                          />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label style={{ fontSize: "12px" }}>Max</label>
                          <input
                            type="number"
                            value={band.max}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                scoreBands: prev.scoreBands.map((b, i) =>
                                  i === idx ? { ...b, max: parseFloat(e.target.value) || 0 } : b
                                )
                              }))
                            }
                            step="0.1"
                          />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label style={{ fontSize: "12px" }}>Label</label>
                          <input
                            type="text"
                            value={band.label}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                scoreBands: prev.scoreBands.map((b, i) =>
                                  i === idx ? { ...b, label: e.target.value } : b
                                )
                              }))
                            }
                            placeholder="e.g., Advanced"
                          />
                        </div>
                        <button
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              scoreBands: prev.scoreBands.filter((_, i) => i !== idx)
                            }))
                          }
                          style={{
                            padding: "6px 8px",
                            background: "rgba(220, 38, 38, 0.1)",
                            border: "1px solid rgba(220, 38, 38, 0.3)",
                            color: "#ef4444",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "14px",
                            alignSelf: "flex-end",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}
                        >
                          {SVG_ICONS["trash"]({ size: 16, color: "#ef4444" })}
                        </button>
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: "12px" }}>Description</label>
                        <textarea
                          value={band.description}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              scoreBands: prev.scoreBands.map((b, i) =>
                                i === idx ? { ...b, description: e.target.value } : b
                              )
                            }))
                          }
                          placeholder="Explanation of this score range"
                          rows="2"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Questions Tab */}
        {activeTab === "questions" && (
        <div className="editor-section" style={{ display: "flex", flexDirection: "column", height: "600px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexShrink: 0 }}>
            <h4>Questions ({formData.questions?.length || 0})</h4>
            <button className="btn-add-question" onClick={handleAddQuestion}>
              + Add Question
            </button>
          </div>

          <div style={{ flex: 1, overflowY: "auto", paddingRight: "8px" }}>
            {formData.questions?.length === 0 ? (
              <p style={{ color: "#7A9CC4", textAlign: "center", padding: "20px" }}>
                No questions yet. Add one to get started.
              </p>
            ) : (
              <div className="questions-list">
                {formData.questions.map((question, index) => (
                <div key={question.id} className="question-card">
                  <div className="question-header">
                    <span className="question-number">Q{index + 1}</span>
                    <div className="question-actions">
                      <button
                        className="btn-move"
                        onClick={() => handleMoveQuestion(question.id, "up")}
                        disabled={index === 0}
                        title="Move up"
                      >
                        ↑
                      </button>
                      <button
                        className="btn-move"
                        onClick={() => handleMoveQuestion(question.id, "down")}
                        disabled={index === formData.questions.length - 1}
                        title="Move down"
                      >
                        ↓
                      </button>
                      <button
                        className="btn-delete-question"
                        onClick={() => handleDeleteQuestion(question.id)}
                        style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        {SVG_ICONS["trash"]({ size: 16, color: "currentColor" })}
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Question Text</label>
                    <textarea
                      value={question.q}
                      onChange={(e) =>
                        handleQuestionChange(question.id, "q", e.target.value)
                      }
                      rows="2"
                    />
                  </div>

                  <div className="options-section">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "12px"
                      }}
                    >
                      <label style={{ margin: 0 }}>Options</label>
                      <button
                        className="btn-add-option"
                        onClick={() => handleAddOption(question.id)}
                      >
                        + Add Option
                      </button>
                    </div>

                    {question.opts?.map((option, optIndex) => (
                      <div key={optIndex} className="option-input-group">
                        <input
                          type="text"
                          value={option.label}
                          onChange={(e) =>
                            handleOptionChange(
                              question.id,
                              optIndex,
                              "label",
                              e.target.value
                            )
                          }
                          placeholder={`Option ${optIndex + 1}`}
                        />
                        {question.type === "scored" && (
                          <input
                            type="number"
                            min="1"
                            max="10"
                            value={option.score || 1}
                            onChange={(e) =>
                              handleOptionChange(
                                question.id,
                                optIndex,
                                "score",
                                parseInt(e.target.value) || 1
                              )
                            }
                            placeholder="Score"
                            style={{ width: "70px" }}
                          />
                        )}
                        {question.opts.length > 1 && (
                          <button
                            className="btn-delete-option"
                            onClick={() =>
                              handleDeleteOption(question.id, optIndex)
                            }
                            style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            {SVG_ICONS["x"]({ size: 16, color: "currentColor" })}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              </div>
            )}

              {/* Add Question Button at Bottom */}
              {formData.questions?.length > 0 && (
                <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
                  <button className="btn-add-question" onClick={handleAddQuestion}>
                    + Add Question
                  </button>
                </div>
              )}
            </div>
        </div>
        )}

        {/* Preview Tab */}
        {activeTab === "preview" && (
        <div className="editor-section">
          <h4>Assessment Preview</h4>
          <p style={{ color: "#7A9CC4", fontSize: "13px", marginBottom: "16px" }}>
            This is how users will see your assessment. Try selecting answers to preview the experience.
          </p>

          {formData.questions?.length === 0 ? (
            <p style={{ color: "#7A9CC4", textAlign: "center", padding: "20px" }}>
              No questions to preview yet.
            </p>
          ) : (
            <div style={{ background: "#0F2440", borderRadius: "8px", padding: "20px" }}>
              <h5 style={{ margin: "0 0 12px 0", color: "#5B9EF0" }}>
                {formData.name}
              </h5>
              {formData.description && (
                <p style={{ color: "#7A9CC4", fontSize: "13px", marginBottom: "20px" }}>
                  {formData.description}
                </p>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                {formData.questions.map((question, index) => (
                  <div
                    key={question.id}
                    style={{
                      background: "#050e1a",
                      padding: "16px",
                      borderRadius: "6px",
                      border: "1px solid rgba(59, 130, 246, 0.15)"
                    }}
                  >
                    <h6 style={{ margin: "0 0 12px 0", color: "#FFF" }}>
                      Question {index + 1}
                    </h6>
                    <p style={{ margin: "0 0 16px 0", color: "#7A9CC4" }}>
                      {question.q}
                    </p>

                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {question.opts?.map((option, optIndex) => (
                        <label
                          key={optIndex}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "8px 12px",
                            background: previewAnswers[question.id] === optIndex ? "rgba(43, 124, 233, 0.2)" : "rgba(59, 130, 246, 0.05)",
                            borderRadius: "4px",
                            border: `1px solid ${previewAnswers[question.id] === optIndex ? "rgba(43, 124, 233, 0.4)" : "rgba(59, 130, 246, 0.15)"}`,
                            cursor: "pointer",
                            transition: "all 0.2s"
                          }}
                        >
                          <input
                            type="radio"
                            name={`question-${question.id}`}
                            checked={previewAnswers[question.id] === optIndex}
                            onChange={() =>
                              setPreviewAnswers((prev) => ({
                                ...prev,
                                [question.id]: optIndex
                              }))
                            }
                            style={{ cursor: "pointer" }}
                          />
                          <span style={{ color: "#FFF" }}>{option.label}</span>
                          {question.type === "scored" && (
                            <span style={{ color: "#7A9CC4", fontSize: "12px", marginLeft: "auto" }}>
                              +{option.score || 1}
                            </span>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: "24px", textAlign: "center" }}>
                <button
                  disabled
                  style={{
                    padding: "10px 20px",
                    background: "rgba(52, 211, 153, 0.2)",
                    border: "1px solid rgba(52, 211, 153, 0.3)",
                    color: "#7A9CC4",
                    borderRadius: "6px",
                    cursor: "not-allowed",
                    fontSize: "14px",
                    fontWeight: "500"
                  }}
                >
                  Submit (Preview Only)
                </button>
              </div>
            </div>
          )}
        </div>
        )}

        {/* Versioning Tab */}
        {activeTab === "versioning" && (
        <div className="editor-section">
          <h4>Versioning</h4>
          <div style={{ background: "#050e1a", padding: "12px", borderRadius: "6px", border: "1px solid rgba(59, 130, 246, 0.15)", marginBottom: "12px" }}>
            <p style={{ margin: "0 0 8px 0", color: "#7A9CC4", fontSize: "13px" }}>
              <strong>Version:</strong> {formData.version || 1}
            </p>
            {formData.parentId && (
              <p style={{ margin: "0 0 8px 0", color: "#7A9CC4", fontSize: "13px" }}>
                <strong>Parent ID:</strong> {formData.parentId}
              </p>
            )}
            <div className="form-group" style={{ marginBottom: 0, display: "flex", alignItems: "center", gap: "8px" }}>
              <input
                type="checkbox"
                checked={formData.isLocked}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    isLocked: e.target.checked,
                    lockedAt: e.target.checked ? new Date().toISOString() : null
                  }))
                }
                id="lock-assessment"
              />
              <label htmlFor="lock-assessment" style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", marginBottom: 0 }}>
                {SVG_ICONS["lock"]({ size: 16, color: "#5B9EF0" })}
                Lock this assessment (require new version for changes)
              </label>
            </div>
            {formData.isLocked && formData.lockedAt && (
              <p style={{ margin: "8px 0 0 0", color: "#7A9CC4", fontSize: "12px" }}>
                Locked at: {new Date(formData.lockedAt).toLocaleString()}
              </p>
            )}
          </div>
        </div>
        )}

        {/* Actions */}
        <div className="editor-actions">
          <button
            className={`btn-live-toggle ${formData.live ? "active" : ""}`}
            onClick={handleToggleLive}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}
          >
            {formData.live ? (
              <>
                {SVG_ICONS["check-circle"]({ size: 16, color: "#22C55E" })}
                LIVE
              </>
            ) : (
              <>
                {SVG_ICONS["circle"]({ size: 16, color: "#9CA3AF" })}
                PUBLISH
              </>
            )}
          </button>
          <button className="btn-save-editor" onClick={handleSave}>
            Save Changes
          </button>
          <button className="btn-cancel-editor" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssessmentEditModal;
